import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Button, Grid, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useOfferFilterParams } from 'hooks';

import { useFilterTags } from './useFilterTags';

import './applied-filter-bar.less';

/** Etiket özetinde gösterilen değer sayısı; fazlası "+N" olur. */
const SUMMARY_VALUE_COUNT = 2;

/** Masaüstünde çubuğun kapladığı en fazla satır. */
const MAX_ROWS = 2;

/** `.applied-filter-bar__row` içindeki `gap` ile aynı olmalı. */
const ROW_GAP = 8;

/** Öğeleri `width` genişliğindeki satırlara soldan sağa dizer ve kaç satır tuttuğunu döner (flex-wrap gibi). */
const countRows = (widths, width) =>
  widths.reduce(
    ({ rows, used }, itemWidth) => {
      if (used === 0) {
        return { rows: Math.max(rows, 1), used: itemWidth };
      }
      if (used + ROW_GAP + itemWidth <= width) {
        return { rows, used: used + ROW_GAP + itemWidth };
      }
      return { rows: rows + 1, used: itemWidth };
    },
    { rows: 0, used: 0 }
  ).rows;

/**
 * İki satıra sığan etiket sayısı. Hepsi sığmıyorsa "+N filtre daha" düğmesine yer bırakılır. Başlık ve "Tümünü
 * temizle" her zaman görünür.
 */
const countFittingTags = ({ width, labelWidth, tagWidths, moreWidth, clearWidth }) => {
  const fits = (widths) => countRows([labelWidth, ...widths, clearWidth], width) <= MAX_ROWS;
  if (fits(tagWidths)) {
    return tagWidths.length;
  }
  let count = tagWidths.length - 1;
  while (count > 0 && !fits([...tagWidths.slice(0, count), moreWidth])) {
    count -= 1;
  }
  return count;
};

/**
 * Gizli ölçüm katmanındaki öğelerin doğal genişliklerinden iki satıra sığan etiket sayısını hesaplar. Her render'da
 * (adlar yüklenince genişlik değişir) ve satırın genişliği değiştiğinde yeniden ölçer.
 */
const useFittingTagCount = ({ rowRef, measureRef, total, isEnabled }) => {
  const [count, setCount] = useState(total);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const layer = measureRef.current;
    if (!row || !layer) {
      return;
    }
    const width = row.clientWidth;
    const widthOf = (selector) => Math.min(layer.querySelector(selector)?.offsetWidth ?? 0, width);
    setCount(
      countFittingTags({
        width,
        labelWidth: widthOf('[data-measure="label"]'),
        tagWidths: [...layer.querySelectorAll('[data-measure="tag"]')].map((node) => Math.min(node.offsetWidth, width)),
        moreWidth: widthOf('[data-measure="more"]'),
        clearWidth: widthOf('[data-measure="clear"]'),
      })
    );
  }, [rowRef, measureRef]);

  useLayoutEffect(() => {
    if (isEnabled) {
      measure();
    }
  });

  useEffect(() => {
    const row = rowRef.current;
    if (!isEnabled || !row) {
      return undefined;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(row);
    return () => observer.disconnect();
  }, [isEnabled, measure, rowRef]);

  return isEnabled ? count : total;
};

const ValueText = ({ text }) =>
  text === null ? (
    <span className="applied-filter-tag__loading">
      <span className="applied-filter-bar__sr-only">Yükleniyor</span>
    </span>
  ) : (
    text
  );

/** Etiketin görünen metni: "Alan: d1, d2 +N". İşaret alanlarında (iade edilmişler) yalnız alan adı. */
const TagSummary = ({ field, items }) => {
  if (field.kind === 'flag') {
    return field.label;
  }
  const shown = items.slice(0, SUMMARY_VALUE_COUNT);
  const restCount = items.length - shown.length;
  return (
    <>
      {field.label}:{' '}
      {shown.map((item, index) => (
        <span key={String(item.value)}>
          {index > 0 && ', '}
          <ValueText text={item.text} />
        </span>
      ))}
      {restCount > 0 && <span className="applied-filter-tag__rest"> +{restCount}</span>}
    </>
  );
};

const TagValues = ({ tag, onRemoveValue, onRemoveField, onEdit }) => {
  const { field, items } = tag;
  const isMulti = field.kind === 'multi';
  return (
    <div
      className="applied-filter-values"
      role="dialog"
      aria-label={`${field.label} seçimleri`}
    >
      <div className="applied-filter-values__header">{isMulti ? `${field.label} · ${items.length} seçili` : field.label}</div>
      <ul className="applied-filter-values__list">
        {items.map((item) => (
          <li
            key={String(item.value)}
            className="applied-filter-values__item"
          >
            <span>
              <ValueText text={item.text} />
            </span>
            <Button
              type="text"
              size="small"
              className="gx-mb-0"
              icon={<CloseOutlined />}
              aria-label={`${item.text ?? 'Değeri'} kaldır`}
              onClick={() => (isMulti ? onRemoveValue(item.value) : onRemoveField())}
            />
          </li>
        ))}
      </ul>
      <div className="applied-filter-values__footer">
        <Button
          type="link"
          className="gx-mb-0"
          onClick={onEdit}
        >
          Düzenle
        </Button>
        {isMulti && items.length > 1 && (
          <Button
            type="link"
            className="gx-mb-0"
            onClick={onRemoveField}
          >
            Hepsini kaldır
          </Button>
        )}
      </div>
    </div>
  );
};

const FilterTag = ({ tag, onRemoveValue, onRemoveField, onEdit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { field } = tag;

  return (
    <span className={`applied-filter-tag${isOpen ? ' applied-filter-tag--open' : ''}`}>
      <Popover
        trigger="click"
        placement="bottomLeft"
        open={isOpen}
        onOpenChange={setIsOpen}
        overlayClassName="applied-filter-values-popover"
        content={
          <TagValues
            tag={tag}
            onRemoveValue={onRemoveValue}
            onRemoveField={onRemoveField}
            onEdit={() => {
              setIsOpen(false);
              onEdit();
            }}
          />
        }
      >
        <button
          type="button"
          className="applied-filter-tag__text"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <TagSummary {...tag} />
        </button>
      </Popover>
      <button
        type="button"
        className="applied-filter-tag__remove"
        aria-label={`${field.label} filtresini kaldır`}
        onClick={onRemoveField}
      >
        <CloseOutlined />
      </button>
    </span>
  );
};

/** Ölçüm katmanındaki etiket: görünen etiketle aynı kutu, etkileşimsiz. */
const MeasureTag = ({ tag }) => (
  <span
    className="applied-filter-tag"
    data-measure="tag"
  >
    <span className="applied-filter-tag__text">
      <TagSummary {...tag} />
    </span>
    <span className="applied-filter-tag__remove">
      <CloseOutlined />
    </span>
  </span>
);

/**
 * Teklif listesinin üstünde uygulanmış filtrelerin etiketleri. Filtreleri URL'den okur; etiketten yapılan her kaldırma
 * URL'ye anında yazılır. Arama metni, sıralama ve rota kısıtları kullanıcı filtresi olmadığı için etiket olmaz ve
 * "Tümünü temizle"de korunur. Filtre yokken hiçbir şey render etmez.
 *
 * Masaüstünde en fazla iki satır kaplar, fazlası "+N filtre daha" arkasına katlanır; 576 px'ten dar ekranda etiketler
 * tek satırda yatay kayar.
 *
 * `onEditGroup`: değer listesindeki "Düzenle"ye basılınca alanın grubuyla (`FILTER_GROUPS[].key`) çağrılır.
 */
export const AppliedFilterBar = ({ onEditGroup }) => {
  const { fields, values, removeField, removeValue, clearAll } = useOfferFilterParams();
  const tags = useFilterTags(fields, values);
  const { sm } = Grid.useBreakpoint();
  const isMobile = sm === false;
  const [isExpanded, setIsExpanded] = useState(false);
  const rowRef = useRef(null);
  const measureRef = useRef(null);

  const fittingCount = useFittingTagCount({ rowRef, measureRef, total: tags.length, isEnabled: !isMobile && tags.length > 0 });
  const hiddenCount = tags.length - fittingCount;
  const visibleTags = isExpanded ? tags : tags.slice(0, fittingCount);

  if (tags.length === 0) {
    return null;
  }

  const clearButton = (
    <Button
      type="link"
      className="gx-mb-0 applied-filter-bar__clear"
      onClick={clearAll}
    >
      Tümünü temizle
    </Button>
  );

  return (
    <div
      className={`applied-filter-bar${isMobile ? ' applied-filter-bar--mobile' : ''}`}
      role="region"
      aria-label="Uygulanan filtreler"
    >
      <div
        ref={rowRef}
        className="applied-filter-bar__row"
      >
        {!isMobile && <span className="applied-filter-bar__label">Uygulanan filtreler:</span>}
        {visibleTags.map((tag) => (
          <FilterTag
            key={tag.field.key}
            tag={tag}
            onRemoveField={() => removeField(tag.field.key)}
            onRemoveValue={(value) => removeValue(tag.field.key, value)}
            onEdit={() => onEditGroup(tag.field.group)}
          />
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            className="applied-filter-bar__more"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? 'Daha az göster' : `+${hiddenCount} filtre daha`}
          </button>
        )}
        {!isMobile && clearButton}
      </div>
      {isMobile && <div className="applied-filter-bar__mobile-actions">{clearButton}</div>}
      {!isMobile && (
        <div
          ref={measureRef}
          className="applied-filter-bar__measure"
          aria-hidden="true"
        >
          <span
            className="applied-filter-bar__label"
            data-measure="label"
          >
            Uygulanan filtreler:
          </span>
          {tags.map((tag) => (
            <MeasureTag
              key={tag.field.key}
              tag={tag}
            />
          ))}
          <span
            className="applied-filter-bar__more"
            data-measure="more"
          >
            {`+${tags.length} filtre daha`}
          </span>
          <span
            className="ant-btn ant-btn-link applied-filter-bar__clear"
            data-measure="clear"
          >
            Tümünü temizle
          </span>
        </div>
      )}
    </div>
  );
};
